import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ExecutiveDashboard from './ExecutiveDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Users, Calendar, FileText, Clock, TrendingUp, Building2,
  UserCheck, AlertTriangle, CheckCircle, XCircle, ArrowRight,
  Briefcase, GraduationCap, DollarSign, Cake, Gift, MapPin,
  Megaphone, ChevronRight, Fingerprint, BookOpen, Shield
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

import { API_BASE as API_URL } from '../lib/apiBase';
const ROLE_DASHBOARD_CONFIG = {
  super_admin: {
    showEntityStats: true,
    showAllEmployees: true,
    showAllLeaves: true,
    showPayroll: true,
    showUserApprovals: true,
    showComplianceMetrics: true,
    title: 'Super Admin Dashboard',
    subtitle: 'Complete system overview'
  },
  admin: {
    showEntityStats: true,
    showAllEmployees: true,
    showAllLeaves: true,
    showPayroll: true,
    showUserApprovals: true,
    showComplianceMetrics: true,
    title: 'Admin Dashboard',
    subtitle: 'Entity-wide management'
  },
  hr: {
    showEntityStats: false,
    showAllEmployees: true,
    showAllLeaves: true,
    showPayroll: true,
    showUserApprovals: true,
    showComplianceMetrics: false,
    title: 'HR Dashboard',
    subtitle: 'Human resources management'
  },
  principal: {
    showEntityStats: false,
    showAllEmployees: true,
    showAllLeaves: true,
    showPayroll: false,
    showUserApprovals: false,
    showComplianceMetrics: true,
    title: 'Principal Dashboard',
    subtitle: 'Institution-wide department oversight'
  },
  dean: {
    showEntityStats: false,
    showAllEmployees: true,
    showAllLeaves: true,
    showPayroll: false,
    showUserApprovals: false,
    showComplianceMetrics: true,
    title: 'Dean Dashboard',
    subtitle: 'Academic oversight'
  },
  hod: {
    showEntityStats: false,
    showAllEmployees: false,
    showAllLeaves: false,
    showPayroll: false,
    showUserApprovals: false,
    showComplianceMetrics: false,
    title: 'HOD Dashboard',
    subtitle: 'Department management'
  },
  employee: {
    showEntityStats: false,
    showAllEmployees: false,
    showAllLeaves: false,
    showPayroll: false,
    showUserApprovals: false,
    showComplianceMetrics: false,
    title: 'My Dashboard',
    subtitle: 'Personal overview'
  },
  finance: {
    showEntityStats: false,
    showAllEmployees: true,
    showAllLeaves: false,
    showPayroll: true,
    showUserApprovals: false,
    showComplianceMetrics: false,
    title: 'Finance Dashboard',
    subtitle: 'Payroll & financial management'
  }
};

const CHART_COLORS = ['#DC2626', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

export default function Dashboard() {
  const { selectedEntity, entities } = useOutletContext() || {};
  const { user, hasPermission } = useAuth();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myAttendance, setMyAttendance] = useState(null);
  const [instituteStats, setInstituteStats] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [events, setEvents] = useState([]);
  const [myPhotoUrl, setMyPhotoUrl] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [compOffCredits, setCompOffCredits] = useState([]);
  const [myAttStatus, setMyAttStatus] = useState(null);
  const [myPayslip, setMyPayslip] = useState(null);
  const [recentNotices, setRecentNotices] = useState([]);
  const [loginStatus, setLoginStatus] = useState(null);

  const userRole = user?.role || 'employee';
  const config = ROLE_DASHBOARD_CONFIG[userRole] || ROLE_DASHBOARD_CONFIG.employee;
  const isSuperAdmin = userRole === 'super_admin';
  const isHROrAbove = ['hr', 'admin', 'super_admin', 'principal'].includes(userRole);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedEntity, user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem('hrms-token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      
      // Fetch based on role
      const requests = [];
      
      // Always fetch dashboard stats (role-filtered by backend)
      requests.push(
        fetch(`${API_URL}/api/dashboard/stats${selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : ''}`, 
          { headers, credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
      );
      
      // Employee-specific: own leaves and attendance
      if (userRole === 'employee') {
        requests.push(
          fetch(`${API_URL}/api/leaves`, { headers, credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
        );
      } else {
        requests.push(Promise.resolve([]));
      }
      
      // Approvers: pending approvals
      if (['hod', 'principal', 'hr', 'admin', 'super_admin'].includes(userRole)) {
        requests.push(
          fetch(`${API_URL}/api/leaves/my-approvals`, { headers, credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
        );
      } else {
        requests.push(Promise.resolve([]));
      }

      // Super Admin: fetch all employees for institute breakdown
      if (isSuperAdmin) {
        const empUrl = selectedEntity?.entity_id 
          ? `${API_URL}/api/employees?entity_id=${selectedEntity.entity_id}`
          : `${API_URL}/api/employees`;
        requests.push(
          fetch(empUrl, { headers, credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
        );
      } else {
        requests.push(Promise.resolve([]));
      }

      // Fetch birthdays for HR and above
      if (isHROrAbove) {
        requests.push(
          fetch(`${API_URL}/api/birthdays/today`, { headers, credentials: 'include' })
            .then(r => r.ok ? r.json() : { birthdays: [] })
        );
        requests.push(
          fetch(`${API_URL}/api/birthdays/upcoming?days=7`, { headers, credentials: 'include' })
            .then(r => r.ok ? r.json() : { birthdays: [] })
        );
      } else {
        requests.push(Promise.resolve({ birthdays: [] }));
        requests.push(Promise.resolve({ birthdays: [] }));
      }

      // Fetch events for all roles
      requests.push(
        fetch(`${API_URL}/api/events/today`, { headers, credentials: 'include' })
          .then(r => r.ok ? r.json() : { events: [] })
      );
      
      const [statsData, leavesData, approvalsData, employeesData, todayBdayData, upcomingBdayData, eventsData] = await Promise.all(requests);
      
      if (statsData) setStats(statsData);
      if (leavesData) setMyLeaves(leavesData);
      if (approvalsData) setPendingApprovals(approvalsData);
      if (todayBdayData) setTodayBirthdays(todayBdayData.birthdays || []);
      if (upcomingBdayData) setUpcomingBirthdays(upcomingBdayData.birthdays || []);
      if (eventsData) setEvents(eventsData.events || []);

      // Fetch current user's photo
      if (user?.user_id || user?.email) {
        try {
          // Try user-level photo first
          if (user?.user_id) {
            const userPhotoRes = await fetch(`${API_URL}/api/users/${user.user_id}/photo`);
            if (userPhotoRes.ok) {
              setMyPhotoUrl(`${API_URL}/api/users/${user.user_id}/photo`);
            } else {
              // Fallback: check employee record
              const empRes = await fetch(`${API_URL}/api/employees${selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : ''}`, { headers, credentials: 'include' });
              if (empRes.ok) {
                const emps = await empRes.json();
                const me = emps.find(e => e.email === user.email);
                if (me?.profile_picture) setMyPhotoUrl(`${API_URL}${me.profile_picture}`);
              }
            }
          }
        } catch {}
      }
      
      if (employeesData && isSuperAdmin) {
        setAllEmployees(employeesData);
        // Calculate institute-wise stats
        if (entities && entities.length > 0) {
          const instStats = entities.map(entity => {
            const entityEmployees = employeesData.filter(emp => emp.entity_id === entity.entity_id);
            const activeEmployees = entityEmployees.filter(emp => emp.is_active !== false);
            const departments = [...new Set(entityEmployees.map(emp => emp.department).filter(Boolean))];
            return {
              entity_id: entity.entity_id,
              name: entity.name,
              code: entity.code,
              type: entity.type,
              compliance_body: entity.compliance_body,
              total_employees: entityEmployees.length,
              active_employees: activeEmployees.length,
              departments: departments.length,
              hr_assigned: entity.hr_user_id ? true : false
            };
          });
          setInstituteStats(instStats);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }

    // Fetch leave balances for current user
    try {
      const balRes = await fetch(`${API_URL}/api/me/leave-balance`, { headers, credentials: 'include' });
      if (balRes.ok) setLeaveBalances(await balRes.json());
    } catch {}

    // Comp-off credits (worked-on date + 1-month expiry visibility)
    try {
      const coRes = await fetch(`${API_URL}/api/comp-off/credits`, { headers, credentials: 'include' });
      if (coRes.ok) {
        const cs = await coRes.json();
        if (Array.isArray(cs)) setCompOffCredits(cs);
      }
    } catch {}

    // Employee-specific: attendance status, payslip, notices
    if (userRole === 'employee') {
      try {
        const [attRes, payRes, noticeRes] = await Promise.all([
          fetch(`${API_URL}/api/me/attendance-status`, { headers, credentials: 'include' }),
          fetch(`${API_URL}/api/me/payslip-summary`, { headers, credentials: 'include' }),
          fetch(`${API_URL}/api/notices?limit=3`, { headers, credentials: 'include' }).catch(() => null)
        ]);
        if (attRes.ok) setMyAttStatus(await attRes.json());
        if (payRes.ok) setMyPayslip(await payRes.json());
        if (noticeRes?.ok) {
          const noticeData = await noticeRes.json();
          setRecentNotices(Array.isArray(noticeData) ? noticeData.slice(0, 3) : []);
        }
      } catch {}
    }

    // Fetch login status for admin/hr/principal
    if (['super_admin', 'admin', 'hr', 'principal'].includes(userRole)) {
      try {
        const entityParam = selectedEntity?.entity_id ? `?entity_id=${selectedEntity.entity_id}` : '';
        const lsRes = await fetch(`${API_URL}/api/dashboard/login-status${entityParam}`, { headers, credentials: 'include' });
        if (lsRes.ok) setLoginStatus(await lsRes.json());
      } catch {}
    }
  };

  const renderEmployeeDashboard = () => {
    const pendingLeaves = myLeaves.filter(l => ['pending', 'pending_l2', 'pending_l3'].includes(l.status)).length;
    const approvedLeaves = myLeaves.filter(l => l.status === 'approved').length;
    const todayAtt = myAttStatus?.today;
    const isCheckedIn = todayAtt?.status === 'checked_in' || (todayAtt?.check_in && !todayAtt?.check_out);
    const isComplete = todayAtt?.check_in && todayAtt?.check_out;

    return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Row 1: Key Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Attendance Status */}
        <Card className="stat-card border-l-4 border-l-blue-500" data-testid="today-attendance-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Today</p>
                <p className="text-lg font-bold mt-1">
                  {isComplete ? 'Day Done' : isCheckedIn ? 'Checked In' : 'Not Checked In'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {todayAtt?.check_in ? `In: ${todayAtt.check_in}` : new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {todayAtt?.check_out ? ` · Out: ${todayAtt.check_out}` : ''}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : isCheckedIn ? 'bg-blue-100' : 'bg-slate-100'}`}>
                {isComplete ? <CheckCircle className="w-5 h-5 text-green-600" /> : isCheckedIn ? <Clock className="w-5 h-5 text-blue-600" /> : <MapPin className="w-5 h-5 text-slate-400" />}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leave Summary */}
        <Card className="stat-card border-l-4 border-l-amber-500" data-testid="leave-summary-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Leaves</p>
                <p className="text-lg font-bold mt-1">{pendingLeaves} Pending</p>
                <p className="text-xs text-slate-400 mt-0.5">{approvedLeaves} approved · {myLeaves.length} total</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payslip */}
        <Card className="stat-card border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.location.href = '/payroll'} data-testid="payslip-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Latest Payslip</p>
                {myPayslip ? (
                  <>
                    <p className="text-lg font-bold mt-1">{myPayslip.pay_period || 'Available'}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Net: {myPayslip.net_salary ? `₹${myPayslip.net_salary.toLocaleString('en-IN')}` : 'View details'}</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold mt-1 text-slate-400">--</p>
                    <p className="text-xs text-slate-400 mt-0.5">No payslip yet</p>
                  </>
                )}
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Info */}
        <Card className="stat-card border-l-4 border-l-[#DC2626]" data-testid="profile-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">My Profile</p>
                <p className="text-lg font-bold mt-1 truncate">{myAttStatus?.designation || user?.role?.replace('_', ' ')}</p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{myAttStatus?.department || 'Department'}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#DC2626]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Leave Balance (compact) + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Leave Balance — compact */}
        {leaveBalances.length > 0 && (
          <Card className="lg:col-span-2" data-testid="leave-balance-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#DC2626]" />
                Leave Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {leaveBalances.map(lb => {
                  // always trust the server-clamped balance — total-used can go negative
                  // for staff with no policy (total 0) and looked like a data mismatch
                  const total = Number(lb.total) || 0;
                  const remaining = lb.balance !== undefined && lb.balance !== null
                    ? Number(lb.balance) || 0
                    : Math.max(total - (Number(lb.used) || 0), 0);
                  const pct = total > 0 ? (remaining / total) * 100 : 0;
                  return (
                    <div key={lb.leave_type} className="p-2 rounded-lg border bg-white text-center" data-testid={`balance-${lb.leave_type}`}>
                      <p className="text-[10px] font-medium text-slate-500 uppercase truncate">{lb.leave_type.replace(/_/g, ' ')}</p>
                      <p className={`text-xl font-bold mt-0.5 ${pct > 50 ? 'text-emerald-600' : pct > 20 ? 'text-amber-600' : 'text-red-600'}`}>{remaining}</p>
                      <p className="text-[10px] text-slate-400">of {total}</p>
                    </div>
                  );
                })}
              </div>
              {compOffCredits.length > 0 ? (
                <div className="mt-3 border-t pt-2" data-testid="comp-off-validity-panel">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Comp-Off Validity (1 month from worked date)</p>
                  <div className="space-y-1">
                    {compOffCredits.slice(0, 5).map(c => {
                      const daysLeft = Math.ceil((new Date(c.expires_at) - Date.now()) / 86400000);
                      const noExpiry = (c.expires_at || '').startsWith('9999');
                      const expired = !noExpiry && (c.is_expired || c.expired || daysLeft < 0);
                      const used = Number(c.used_days || 0);
                      const left = c.remaining_days != null ? Number(c.remaining_days) : Number(c.days || 0);
                      const fullyUsed = left <= 0;
                      return (
                        <div key={c.credit_id}
                          className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                            fullyUsed ? 'bg-blue-50 text-blue-700'
                              : expired ? 'bg-slate-100 text-slate-400'
                              : daysLeft <= 3 && !noExpiry ? 'bg-red-50' : 'bg-amber-50'}`}
                          data-testid={`comp-off-credit-${c.credit_id}`}>
                          <span className={expired && !fullyUsed ? 'line-through' : ''}>
                            {c.worked_on ? <>Worked on <strong>{c.worked_on}</strong></> : <>Credited <strong>{c.credited_on}</strong></>} · {c.days} day{c.days !== 1 ? 's' : ''}
                            {used > 0 && <span className="text-blue-700"> · {used} taken</span>}
                            {!fullyUsed && !expired && used > 0 && <span className="text-emerald-700"> · {left} left</span>}
                          </span>
                          <span className={`font-medium ${fullyUsed ? 'text-blue-700' : expired ? '' : daysLeft <= 3 && !noExpiry ? 'text-red-600' : 'text-amber-700'}`}>
                            {fullyUsed ? 'Used' : expired ? 'Lapsed' : noExpiry ? 'No expiry' : `Expires ${c.expires_at}${daysLeft <= 3 ? ` (${daysLeft}d left!)` : ''}`}
                          </span>
                        </div>
                      );
                    })}
                    {compOffCredits.length > 5 && (
                      <p className="text-[10px] text-slate-400 px-2">+ {compOffCredits.length - 5} more — see the Leaves page</p>
                    )}
                  </div>
                </div>
              ) : (() => {
                const co = leaveBalances.find(lb => (lb.leave_type || '').toLowerCase().includes('compensatory'));
                const coRemaining = co ? (co.total || 0) - (co.used || 0) : 0;
                return coRemaining > 0 ? (
                  <div className="mt-3 border-t pt-2" data-testid="comp-off-no-expiry-note">
                    <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                      You have {coRemaining} comp-off day(s) but no worked-date on record, so the expiry date can't be shown. Ask HR to re-credit it with the holiday date you worked.
                    </p>
                  </div>
                ) : null;
              })()}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card data-testid="quick-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button className="w-full justify-start btn-primary" size="sm" onClick={() => window.location.href = '/leaves'}>
              <FileText className="w-4 h-4 mr-2" /> Apply Leave
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => window.location.href = '/attendance'}>
              <Fingerprint className="w-4 h-4 mr-2" /> Mark Attendance
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => window.location.href = '/payroll'}>
              <DollarSign className="w-4 h-4 mr-2" /> View Salary Slips
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => window.location.href = '/documents'}>
              <BookOpen className="w-4 h-4 mr-2" /> My Documents
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Recent Leave Requests (compact) + Notices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Leaves — compact */}
        <Card data-testid="recent-leaves-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#DC2626]" />
                Recent Leave Requests
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-[#DC2626]" onClick={() => window.location.href = '/leaves'}>
                View All <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {myLeaves.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-4">No leave requests yet</p>
            ) : (
              <div className="space-y-2">
                {myLeaves.slice(0, 4).map(leave => {
                  const sc = {
                    pending: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', label: 'Pending HOD' },
                    pending_l2: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', label: 'Pending Principal' },
                    pending_l3: { bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', label: 'Pending HR' },
                    approved: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Approved' },
                    rejected: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Rejected' },
                  }[leave.status] || { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', label: leave.status };
                  return (
                    <div key={leave.leave_id} className={`flex items-center justify-between p-2.5 rounded-lg border ${sc.bg}`} data-testid={`leave-req-${leave.leave_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{leave.leave_type}</span>
                          <span className="text-xs text-slate-400">{leave.days}d</span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {new Date(leave.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {new Date(leave.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <Badge className={`${sc.bg} ${sc.text} border text-[10px] shrink-0`}>{sc.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Notices */}
        <Card data-testid="notices-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-[#DC2626]" />
                Recent Notices
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-[#DC2626]" onClick={() => window.location.href = '/notices'}>
                View All <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentNotices.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-4">No recent notices</p>
            ) : (
              <div className="space-y-2">
                {recentNotices.map(notice => (
                  <div key={notice.notice_id || notice._id} className="flex items-start gap-3 p-2.5 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="min-w-[32px] h-8 rounded bg-[#DC2626]/10 flex items-center justify-center text-[#DC2626]">
                      <Megaphone className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{notice.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-1">{notice.content || notice.description || ''}</p>
                      {notice.created_at && <p className="text-[10px] text-slate-400 mt-0.5">{new Date(notice.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Events */}
      {events.length > 0 && (
        <Card data-testid="events-highlights">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#DC2626]" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {events.map(evt => (
                <div key={evt.event_id} className="flex items-start gap-3 p-3 rounded-lg border bg-gradient-to-r from-red-50 to-orange-50 border-red-100">
                  <div className="min-w-[40px] h-10 rounded-lg bg-[#DC2626]/10 flex flex-col items-center justify-center text-[#DC2626]">
                    <span className="text-xs font-bold">{new Date(evt.event_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric' })}</span>
                    <span className="text-[9px] uppercase">{new Date(evt.event_date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' })}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{evt.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{evt.description}</p>
                    <Badge className="mt-1 text-[10px] bg-red-100 text-red-700">{evt.event_type}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    );
  };

  const renderApproverDashboard = () => (
    <div className="space-y-6">
      {/* Stats for Approvers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {config.showAllEmployees && (
          <Card className="stat-card" data-testid="total-employees-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Employees</p>
                  <p className="text-2xl font-bold">{stats?.total_employees || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="stat-card border-2 border-[#DC2626]" data-testid="pending-approvals-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approvals</p>
                <p className="text-2xl font-bold text-[#DC2626]">{pendingApprovals.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-[#DC2626]" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Present Today</p>
                <p className="text-2xl font-bold text-green-600">{stats?.present_today || 0}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        {config.showUserApprovals && (
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Users</p>
                  <p className="text-2xl font-bold text-amber-600">{stats?.pending_users || 0}</p>
                </div>
                <Briefcase className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* My Leave Balance (for HR/Admin/HOD/Principal too) */}
      {leaveBalances.length > 0 && (
        <Card data-testid="approver-leave-balance">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#DC2626]" />
              My Leave Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {leaveBalances.map(lb => {
                const total = Number(lb.total) || 0;
                const used = Number(lb.used) || 0;
                const remaining = lb.balance !== undefined && lb.balance !== null ? Number(lb.balance) || 0 : Math.max(total - used, 0);
                const pct = total > 0 ? (remaining / total) * 100 : 0;
                return (
                  <div key={lb.leave_type} className="p-3 rounded-lg border bg-white">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{lb.leave_type.replace(/_/g, ' ')}</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-bold text-slate-900">{remaining}</span>
                      <span className="text-sm text-slate-400">/ {total}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                      <div className={`h-full rounded-full ${pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`} style={{width: `${Math.max(pct, 0)}%`}} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{used} used</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <Card className="border-2 border-[#DC2626]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#DC2626]">
              <AlertTriangle className="w-5 h-5" />
              Action Required - Pending Approvals ({pendingApprovals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovals.slice(0, 5).map(leave => (
                <div key={leave.leave_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium">{leave.employee_name || leave.employee_id}</p>
                    <p className="text-sm text-slate-600">{leave.leave_type} • {leave.days} days</p>
                  </div>
                  <Button size="sm" className="btn-primary" onClick={() => window.location.href = '/leaves'}>
                    Review <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Login Status - Password Change Tracking */}
      {loginStatus && (
        <Card data-testid="login-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#DC2626]" />
              Employee Login & Password Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-700">{loginStatus.total_employees}</p>
                <p className="text-xs text-blue-600">Total Staff</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-700">{loginStatus.has_account}</p>
                <p className="text-xs text-slate-600">Have Accounts</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                <p className="text-2xl font-bold text-green-700">{loginStatus.changed_password}</p>
                <p className="text-xs text-green-600">Password Changed</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                <p className="text-2xl font-bold text-amber-700">{loginStatus.pending_password_change}</p>
                <p className="text-xs text-amber-600">Pending Change</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                <p className="text-2xl font-bold text-red-700">{loginStatus.no_account}</p>
                <p className="text-xs text-red-600">No Account</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-600">Password Change Progress</span>
                <span className="font-semibold text-slate-800">{loginStatus.password_change_rate}%</span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all" style={{width: `${loginStatus.password_change_rate}%`}} />
              </div>
            </div>

            {/* Pending List */}
            {loginStatus.pending_list?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Employees Yet to Change Password ({loginStatus.pending_password_change})</p>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Employee</TableHead>
                        <TableHead className="text-xs">Department</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loginStatus.pending_list.map(emp => (
                        <TableRow key={emp.employee_id}>
                          <TableCell className="text-sm font-medium">{emp.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{emp.department}</TableCell>
                          <TableCell className="text-xs text-slate-400">{emp.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Section */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* Events Highlights */}
        {events.length > 0 && (
          <Card className="md:col-span-2" data-testid="approver-events">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#DC2626]" />
                Upcoming Events & Highlights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {events.map(evt => (
                  <div key={evt.event_id} className="flex items-start gap-3 p-3 rounded-lg border bg-gradient-to-r from-red-50 to-orange-50 border-red-100">
                    <div className="min-w-[40px] h-10 rounded-lg bg-[#DC2626]/10 flex flex-col items-center justify-center text-[#DC2626]">
                      <span className="text-xs font-bold">{new Date(evt.event_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric' })}</span>
                      <span className="text-[9px] uppercase">{new Date(evt.event_date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' })}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{evt.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-2">{evt.description}</p>
                      <Badge className="mt-1 text-[10px] bg-red-100 text-red-700">{evt.event_type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {/* Department Distribution */}
        {stats?.department_stats && stats.department_stats.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#DC2626]" />
                {userRole === 'hod' ? 'Department Overview' : 'Employee by Department'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.department_stats.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#DC2626" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Category Distribution */}
        {stats?.category_stats && stats.category_stats.length > 0 && config.showAllEmployees && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#DC2626]" />
                Employee Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.category_stats}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="_id"
                    label={({ _id, percent }) => `${_id?.substring(0, 10) || ''}... ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.category_stats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Entity Stats (Super Admin Only) */}
      {config.showEntityStats && stats?.entity_stats && stats.entity_stats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#DC2626]" />
              Employees by Entity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.entity_stats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="_id" type="category" width={150} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Super Admin Consolidated View
  const renderSuperAdminConsolidated = () => {
    const totalEmployees = allEmployees.length;
    const activeEmployees = allEmployees.filter(e => e.is_active !== false).length;
    const totalDepartments = [...new Set(allEmployees.map(e => e.department).filter(Boolean))].length;
    const teachingStaff = allEmployees.filter(e => e.category === 'Teaching').length;
    const nonTeachingStaff = allEmployees.filter(e => e.category === 'Non-Teaching').length;

    return (
      <div className="space-y-6">
        {/* Consolidated Stats - Top Level */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 overflow-hidden">
          <Card className="stat-card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs md:text-sm text-blue-100 truncate">Total Institutes</p>
                  <p className="text-2xl md:text-3xl font-bold">{instituteStats.length}</p>
                </div>
                <Building2 className="w-6 h-6 md:w-10 md:h-10 text-blue-200 shrink-0 ml-2" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs md:text-sm text-green-100 truncate">Total Employees</p>
                  <p className="text-2xl md:text-3xl font-bold">{totalEmployees}</p>
                </div>
                <Users className="w-6 h-6 md:w-10 md:h-10 text-green-200 shrink-0 ml-2" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs md:text-sm text-purple-100 truncate">Teaching Staff</p>
                  <p className="text-2xl md:text-3xl font-bold">{teachingStaff}</p>
                </div>
                <GraduationCap className="w-6 h-6 md:w-10 md:h-10 text-purple-200 shrink-0 ml-2" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs md:text-sm text-amber-100 truncate">Non-Teaching</p>
                  <p className="text-2xl md:text-3xl font-bold">{nonTeachingStaff}</p>
                </div>
                <Briefcase className="w-6 h-6 md:w-10 md:h-10 text-amber-200 shrink-0 ml-2" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card bg-gradient-to-br from-red-500 to-red-600 text-white col-span-2 md:col-span-1">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs md:text-sm text-red-100 truncate">Pending Approvals</p>
                  <p className="text-2xl md:text-3xl font-bold">{pendingApprovals.length}</p>
                </div>
                <AlertTriangle className="w-6 h-6 md:w-10 md:h-10 text-red-200 shrink-0 ml-2" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Institute-wise Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#DC2626]" />
              Institute-wise Employee Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Institute</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Compliance</TableHead>
                  <TableHead className="text-center">Total Staff</TableHead>
                  <TableHead className="text-center">Departments</TableHead>
                  <TableHead className="text-center">HR Assigned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instituteStats.map((inst) => (
                  <TableRow key={inst.entity_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {inst.code?.substring(0, 2) || 'IN'}
                        </div>
                        <div>
                          <p className="font-medium">{inst.name}</p>
                          <p className="text-xs text-slate-500">{inst.code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{inst.type || 'Not Set'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        inst.compliance_body === 'AICTE' ? 'bg-purple-100 text-purple-800' :
                        inst.compliance_body === 'UGC' ? 'bg-blue-100 text-blue-800' :
                        inst.compliance_body === 'CBSE/IB' ? 'bg-green-100 text-green-800' :
                        'bg-slate-100 text-slate-800'
                      }>
                        {inst.compliance_body || 'None'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-xl font-bold text-blue-600">{inst.total_employees}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-medium">{inst.departments}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {inst.hr_assigned ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-slate-50 dark:bg-slate-800 font-bold">
                  <TableCell colSpan={3} className="text-right">
                    TOTAL ACROSS ALL INSTITUTES
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-xl font-bold text-green-600">{totalEmployees}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-bold">{totalDepartments}</span>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>

        {/* Institute Distribution Chart */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#DC2626]" />
                Employee Distribution by Institute
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={instituteStats} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="code" type="category" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [value, 'Employees']}
                    labelFormatter={(label) => instituteStats.find(i => i.code === label)?.name || label}
                  />
                  <Bar dataKey="total_employees" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#DC2626]" />
                Staff Category Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Teaching', value: teachingStaff },
                      { name: 'Non-Teaching', value: nonTeachingStaff },
                      { name: 'Other', value: totalEmployees - teachingStaff - nonTeachingStaff }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#8B5CF6" />
                    <Cell fill="#F59E0B" />
                    <Cell fill="#6B7280" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals Section */}
        {pendingApprovals.length > 0 && (
          <Card className="border-2 border-[#DC2626]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#DC2626]">
                <AlertTriangle className="w-5 h-5" />
                Action Required - Pending Approvals ({pendingApprovals.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingApprovals.slice(0, 5).map(leave => (
                  <div key={leave.leave_id} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div>
                      <p className="font-medium">{leave.employee_name || leave.employee_id}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{leave.leave_type} • {leave.days} days</p>
                    </div>
                    <Button size="sm" className="btn-primary" onClick={() => window.location.href = '/leaves'}>
                      Review <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Login Status - Password Change Tracking (Super Admin) */}
        {loginStatus && (
          <Card data-testid="login-status-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#DC2626]" />
                Employee Login & Password Status
                {selectedEntity && <Badge variant="outline" className="ml-2 text-xs">{selectedEntity.name}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                  <p className="text-2xl font-bold text-blue-700">{loginStatus.total_employees}</p>
                  <p className="text-xs text-blue-600">Total Staff</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                  <p className="text-2xl font-bold text-slate-700">{loginStatus.has_account}</p>
                  <p className="text-xs text-slate-600">Have Accounts</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                  <p className="text-2xl font-bold text-green-700">{loginStatus.changed_password}</p>
                  <p className="text-xs text-green-600">Password Changed</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                  <p className="text-2xl font-bold text-amber-700">{loginStatus.pending_password_change}</p>
                  <p className="text-xs text-amber-600">Pending Change</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                  <p className="text-2xl font-bold text-red-700">{loginStatus.no_account}</p>
                  <p className="text-xs text-red-600">No Account</p>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Password Change Progress</span>
                  <span className="font-semibold text-slate-800">{loginStatus.password_change_rate}%</span>
                </div>
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{width: `${loginStatus.password_change_rate}%`}} />
                </div>
              </div>
              {loginStatus.pending_list?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Employees Yet to Change Password ({loginStatus.pending_password_change})</p>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Employee</TableHead>
                          <TableHead className="text-xs">Department</TableHead>
                          <TableHead className="text-xs">Email</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loginStatus.pending_list.map(emp => (
                          <TableRow key={emp.employee_id}>
                            <TableCell className="text-sm font-medium">{emp.name}</TableCell>
                            <TableCell className="text-xs text-slate-500">{emp.department}</TableCell>
                            <TableCell className="text-xs text-slate-400">{emp.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Birthday/Upcoming Section */}
        {(todayBirthdays.length > 0 || upcomingBirthdays.length > 0) && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Today's Birthdays */}
            {todayBirthdays.length > 0 && (
              <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950 dark:to-purple-950">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-pink-600">
                    <Cake className="w-5 h-5" />
                    Today's Birthdays ({todayBirthdays.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {todayBirthdays.map(person => (
                      <div key={person.employee_id} className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                          {person.name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{person.name}</p>
                          <p className="text-sm text-slate-500">{person.department} • {person.designation}</p>
                        </div>
                        <div className="text-2xl">🎂</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Birthdays */}
            {upcomingBirthdays.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-500" />
                    Upcoming Birthdays (Next 7 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {upcomingBirthdays.slice(0, 5).map(person => (
                      <div key={person.employee_id} className="flex items-center justify-between p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-purple-600 dark:text-purple-300 font-medium text-sm">
                            {person.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{person.name}</p>
                            <p className="text-xs text-slate-500">{person.department}</p>
                          </div>
                        </div>
                        <Badge className={
                          person.days_until === 0 ? 'bg-pink-100 text-pink-800' :
                          person.days_until === 1 ? 'bg-orange-100 text-orange-800' :
                          'bg-slate-100 text-slate-800'
                        }>
                          {person.days_until === 0 ? 'Today!' : 
                           person.days_until === 1 ? 'Tomorrow' : 
                           `In ${person.days_until} days`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Present Today</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.present_today || 0}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">On Leave Today</p>
                  <p className="text-2xl font-bold text-amber-600">{stats?.on_leave_today || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Users</p>
                  <p className="text-2xl font-bold text-purple-600">{stats?.pending_users || 0}</p>
                </div>
                <Briefcase className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Departments</p>
                  <p className="text-2xl font-bold text-blue-600">{totalDepartments}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Executive Dashboard for super-admin, chairman, trustee, and any cross-institute viewer.
  // Rendered AFTER all hooks to honor the Rules of Hooks.
  const isExecutive = ['super_admin', 'chairman', 'trustee'].includes(user?.role) ||
    (!user?.entity_id && !['employee', 'hod'].includes(user?.role));
  if (isExecutive) {
    return <ExecutiveDashboard />;
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="relative w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-[#DC2626]/10 flex items-center justify-center shrink-0 ring-2 ring-[#DC2626]/20" data-testid="dashboard-user-photo">
            {myPhotoUrl ? (
              <img src={myPhotoUrl} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#DC2626] font-bold text-lg">
                {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
              Welcome back, <span className="text-[#DC2626]">{user?.name?.split(' ')[0] || 'User'}</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} &bull; {config.title}
            </p>
          </div>
        </div>
        <Badge className="bg-[#DC2626] text-white px-3 py-1 text-sm">
          {userRole.toUpperCase().replace('_', ' ')}
        </Badge>
      </div>

      {/* Role-specific dashboard content */}
      {userRole === 'employee' ? renderEmployeeDashboard() : 
       isSuperAdmin && !selectedEntity ? renderSuperAdminConsolidated() : 
       isSuperAdmin && selectedEntity ? renderApproverDashboard() :
       renderApproverDashboard()}
    </div>
  );
}
